import {
  ActionButton,
  ActionSubmit,
  Modal,
  ModalBody,
  ModalButton,
  ModalButtons,
  ModalCloseX,
  ModalContentForm,
  ModalHeading,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import type {
  ReactNode,
  MouseEventHandler,
  FormEventHandler,
} from "react"

import { DiscardChangesDialog } from "../DiscardChangesDialog"
import { RemoveDialog } from "../RemoveDialog"
import styles from "./AccountInsulinModal.module.scss"
import { AccountInsulinModalContext } from "./AccountInsulinModalContext"
import { emptyAccountInsulin } from "./EditableAccountInsulin"
import { InsulinCategorySelect } from "./InsulinCategorySelect"
import { InsulinFormSelect } from "./InsulinFormSelect"
import { InsulinIsBasalToggle } from "./InsulinIsBasalToggle"
import { InsulinNameSelect } from "./InsulinNameSelect"
import { InsulinPrecisionSelect } from "./InsulinPrecisionSelect"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { InsulinLoadingProvider } from "src/contexts/LoadingContexts"
import {
  useSaveAccountInsulin,
  useDeleteAccountInsulin,
  useEditableType,
  useInsulin,
} from "src/hooks"
import {
  AddInsulinErrorToast,
  AddInsulinSuccessToast,
  EditInsulinErrorToast,
  EditInsulinSuccessToast,
  RemoveInsulinErrorToast,
  RemoveInsulinSuccessToast,
} from "src/toasts"
import type { EditableAccountInsulin } from "src/types"

const polyglotPrefix = "components.account_insulin_modal."

const saveBtnId = "save-insulin-btn"
const cancelBtnId = "cancel-insulin-btn"
const modalTitleId = "edit-modal-title"

interface Props {
  children: ReactNode;
}

export function AccountInsulinModal(props: Props) {
  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const [
    isDiscardOpen,
    setIsDiscardOpen,
  ] = useState<boolean>(false)
  const [
    isRemoveOpen,
    setIsRemoveOpen,
  ] = useState<boolean>(false)
  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState<boolean>(false)

  const {
    current: currentAccountInsulin,
    hasChanged: accountInsulinHasChanged,
    save: resetAccountInsulin,
    update: editAccountInsulin,
  } = useEditableType<EditableAccountInsulin>(emptyAccountInsulin)

  const currentInsulin = useInsulin(currentAccountInsulin?.insulin_id)

  // closes the modal regardless of whether or not changes were saved
  const closeModal = useCallback(
    () => {
      setIsModalOpen(false)
    },
    [setIsModalOpen],
  )

  // opens the modal and fills it w/ the specified account insulin
  const openModal = useCallback(
    (accountInsulin: EditableAccountInsulin = emptyAccountInsulin) => {
      resetAccountInsulin(accountInsulin)
      setIsModalOpen(true)
    },
    [
      setIsModalOpen,
      resetAccountInsulin,
    ],
  )

  // show the warning dialog if changes have been made without saving
  // the only way to open this modal is with "openAccountInsulinModal"
  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (accountInsulinHasChanged) {
          setIsDiscardOpen(true)
        } else {
          setIsModalOpen(false)
        }
      }
    },
    [
      accountInsulinHasChanged,
      setIsModalOpen,
      setIsDiscardOpen,
    ],
  )

  const onEditInsulinCategory = useCallback(
    (e: EditableAccountInsulin) => {
      editAccountInsulin({
        ...e,
        insulin_id: undefined,
        form: undefined,
        precision: undefined,
      })
    },
    [editAccountInsulin],
  )

  const onEditInsulinName = useCallback(
    (e: EditableAccountInsulin) => {
      editAccountInsulin({
        ...e,
        form: undefined,
        precision: undefined,
      })
    },
    [editAccountInsulin],
  )

  const onEditInsulinForm = useCallback(
    (e: EditableAccountInsulin) => {
      editAccountInsulin({
        ...e,
        precision: undefined,
      })
    },
    [editAccountInsulin],
  )

  // will either create or update the account insulin
  const saveAccountInsulin = useSaveAccountInsulin()

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          // TODO change IDs !
          await saveAccountInsulin(currentAccountInsulin)
          closeModal()

          if (currentAccountInsulin.id) {
            showToast(
              "edit-insulin-success-toast",
              EditInsulinSuccessToast,
            )
          } else {
            showToast(
              "add-insulin-success-toast",
              AddInsulinSuccessToast,
            )
          }
        } catch {
          if (currentAccountInsulin.id) {
            showToast(
              "edit-insulin-error-toast",
              EditInsulinErrorToast,
            )
          } else {
            showToast(
              "add-insulin-error-toast",
              AddInsulinErrorToast,
            )
          }
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      showToast,
      closeModal,
      saveAccountInsulin,
      currentAccountInsulin,
      setIsOverlayLoaderVisible,
    ],
  )

  // will remove the account insulin
  const removeAccountInsulin = useDeleteAccountInsulin()

  const remove: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {

      void (async () => {
        if (!currentAccountInsulin.id) {
          throw new Error("cannot delete account insulin that has not been saved")
        }

        setIsOverlayLoaderVisible(true)

        try {
          await removeAccountInsulin(Number(currentAccountInsulin.id))

          showToast(
            "remove-insulin-success-toast",
            RemoveInsulinSuccessToast,
          )

          closeModal()
        } catch {
          showToast(
            "remove-insulin-error-toast",
            RemoveInsulinErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      showToast,
      closeModal,
      removeAccountInsulin,
      currentAccountInsulin,
      setIsOverlayLoaderVisible,
    ],
  )

  // when the remove cta is clicked, open the remove dialog
  const onRemoveClick = useCallback(
    () => {
      setIsRemoveOpen(true)
    },
    [setIsRemoveOpen],
  )

  const isNewAccountInsulin = currentAccountInsulin.id === emptyAccountInsulin.id

  const contextValue = useMemo(
    () => ({
      openAccountInsulinModal: openModal,
    }),
    [openModal],
  )

  return (
    <Modal
      open={isModalOpen}
      onOpenChange={onOpenChange}
    >
      <AccountInsulinModalContext.Provider value={contextValue}>
        {props.children}
      </AccountInsulinModalContext.Provider>
      <InsulinLoadingProvider>
        <ModalContentForm
          onSubmit={onSubmit}
          id={modalTitleId}
        >
          <ModalHeading>
            {isNewAccountInsulin
              ? polyglot.t(`${polyglotPrefix}add_title`)
              : polyglot.t(
                `${polyglotPrefix}edit_title`,
                { name: currentInsulin?.name },
              )
            }
            <ModalCloseX id={cancelBtnId}/>
          </ModalHeading>
          <ModalBody className={styles.modalBody}>
            {isNewAccountInsulin && (
              <InsulinCategorySelect
                currentAccountInsulin={currentAccountInsulin}
                editAccountInsulin={onEditInsulinCategory}
              />
            )}
            {isNewAccountInsulin && (
              <InsulinNameSelect
                currentAccountInsulin={currentAccountInsulin}
                editAccountInsulin={onEditInsulinName}
              />
            )}
            <InsulinFormSelect
              currentAccountInsulin={currentAccountInsulin}
              editAccountInsulin={onEditInsulinForm}
              currentInsulin={currentInsulin}
            />
            <InsulinPrecisionSelect
              currentAccountInsulin={currentAccountInsulin}
              editAccountInsulin={editAccountInsulin}
              currentInsulin={currentInsulin}
            />
            <InsulinIsBasalToggle
              currentAccountInsulin={currentAccountInsulin}
              editAccountInsulin={editAccountInsulin}
            />
            <ModalButtons>
              {isNewAccountInsulin ? (
                <ModalButton
                  stylePreset="secondary"
                  type="button"
                >
                  {polyglot.t(`${polyglotPrefix}cancel_cta`)}
                </ModalButton>
              ) : (
                <ActionButton
                  className={styles.modalMultiButton}
                  stylePreset="secondary"
                  onClick={onRemoveClick}
                  type="button"
                  id="remove-btn"
                >
                  {polyglot.t(`${polyglotPrefix}remove_cta`)}
                </ActionButton>
              )}
              <ActionSubmit
                className={styles.modalMultiButton}
                stylePreset="primary"
                value={polyglot.t(`${polyglotPrefix}save_cta`)}
                disabled={!accountInsulinHasChanged}
                id={saveBtnId}
              />
            </ModalButtons>
          </ModalBody>
          <DiscardChangesDialog
            open={isDiscardOpen}
            setOpen={setIsDiscardOpen}
            discardChanges={closeModal}
          />
          <RemoveDialog
            open={isRemoveOpen}
            setOpen={setIsRemoveOpen}
            remove={remove}
            title={polyglot.t(
              `${polyglotPrefix}remove_heading`,
              { name: currentInsulin?.name },
            )}
          />
        </ModalContentForm>
      </InsulinLoadingProvider>
    </Modal>
  )
}
